// Shared pieces of the Hero 2 look (hero.css): the cmx_ wordmark, the three points, the
// 01/02/03 steps, icons, the small night-shift mark, and rise-in-on-scroll.

import { useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from 'react';
import { Link } from '../router';
import { CmxMark } from '../ui/CmxMark';
import { useTheme } from '../ui/ds/theme';
import welcomeBack from '../art/welcome-back.svg?raw';
import '../hero.css';

export function HxBrand({ to = '/' }: { to?: string }) {
  return (
    <Link to={to} className="hx-brand cmx-lockup" aria-label="cmx home">
      <CmxMark />
      <span className="wm" aria-hidden="true">
        cmx<span className="wm-u">_</span>
      </span>
    </Link>
  );
}

const svg = (children: ReactNode) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

export const Icon = {
  moon: svg(<path d="M16 12.6A7 7 0 0 1 7.4 4a7 7 0 1 0 8.6 8.6z" />),
  bolt: svg(<path d="M11.2 2.5 4.8 11h5l-1 6.5L15.2 9h-5z" />),
  prompt: svg(
    <>
      <rect x="2.5" y="3.5" width="15" height="13" rx="3" />
      <path d="M6.2 8l2.4 2-2.4 2M10.8 12.3h3" />
    </>,
  ),
  bell: svg(
    <>
      <path d="M5 13.5V9a5 5 0 0 1 10 0v4.5l1.5 1.5h-13z" />
      <path d="M8.2 17.2a2 2 0 0 0 3.6 0" />
    </>,
  ),
  display: svg(
    <>
      <rect x="2.5" y="3.5" width="15" height="10.5" rx="2" />
      <path d="M7 17h6M10 14v3" />
    </>,
  ),
  link: svg(
    <>
      <path d="M8.5 11.5a3.5 3.5 0 0 0 5 0l2.3-2.3a3.5 3.5 0 0 0-5-5l-1 1" />
      <path d="M11.5 8.5a3.5 3.5 0 0 0-5 0l-2.3 2.3a3.5 3.5 0 0 0 5 5l1-1" />
    </>,
  ),
  laptop: svg(
    <>
      <rect x="4" y="4" width="12" height="8.5" rx="1.5" />
      <path d="M2 15.5h16" />
    </>,
  ),
  apple: (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M13.6 10.6c0-1.8 1.5-2.7 1.6-2.8-.9-1.3-2.2-1.4-2.7-1.5-1.1-.1-2.2.7-2.8.7-.6 0-1.5-.7-2.4-.7-1.2 0-2.4.7-3 1.9-1.3 2.2-.3 5.5.9 7.3.6.9 1.3 1.9 2.3 1.8.9 0 1.3-.6 2.4-.6s1.4.6 2.4.6c1 0 1.6-.9 2.2-1.8.7-1 1-2 1-2.1 0 0-1.9-.7-1.9-2.8zM11.8 5.2c.5-.6.8-1.4.7-2.2-.7 0-1.6.5-2.1 1.1-.5.5-.9 1.4-.8 2.2.8.1 1.7-.4 2.2-1.1z" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.2 5.2l1.9 1.9 3.8-4.2" />
    </svg>
  ),
  arrow: svg(<path d="M4 10h11M11 6l4 4-4 4" />),
};

/** The three points under a hero: always on, serious power, feels local. */
export function Points({ className = '', style }: { className?: string; style?: CSSProperties }) {
  const items: [string, ReactNode, string, string][] = [
    ['blue', Icon.moon, 'Always on', 'Close the lid. Agents keep going.'],
    ['amber', Icon.bolt, 'Serious power', 'Run it all at once. Mac stays cool.'],
    ['green', Icon.prompt, 'Feels local', 'Jump in any time, right here.'],
  ];
  return (
    <ul className={`hx-points ${className}`} style={style}>
      {items.map(([tone, icon, title, line]) => (
        <li key={title} className="hx-point">
          <span className={`hx-tile hx-tile-${tone}`}>{icon}</span>
          <span className="hx-point-text">
            <b>{title}</b>
            <span>{line}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** 01 Sign in · 02 Magic Setup · 03 Start working; `at` is the current step (1–3), 4 = all done. */
export function Steps({ at, labels = ['Sign in', 'Magic Setup', 'Start working'] }: { at: number; labels?: string[] }) {
  return (
    <ol className="hx-steps" aria-label="Steps">
      {labels.map((l, i) => {
        const n = i + 1;
        const state = n < at ? 'done' : n === at ? 'current' : 'next';
        return (
          <li key={l} data-state={state} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="hx-step-chip">{String(n).padStart(2, '0')}</span>
            {l}
          </li>
        );
      })}
    </ol>
  );
}

/** A story drawing (src/art, CmxArt.swift on the Mac): light drops its dark <style>, dark keeps it, as the Mac does,
 *  without its media query, so Dark picked in Settings wins over a light computer. Only a themed route turns it dark. */
export function StoryArt({ svg, width, height, className }: { svg: string; width: number; height: number; className?: string }) {
  const dark = useTheme() === 'dark' && document.documentElement.dataset.theme === 'dark';
  const src = useMemo(() => {
    const own = dark ? svg.replace(/<style data-theme="dark"[^>]*>/, '<style>') : svg.replace(/<style data-theme="dark"[^>]*>[^<]*<\/style>/, '');
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(own)}`;
  }, [svg, dark]);
  return <img className={className} src={src} width={width} height={height} alt="" />;
}

/** Welcome back to cmx: the machine, still on, the session waiting above it (src/art/welcome-back.svg).
 *  Sign-in routes are never themed, so this is light until they are. */
export const WelcomeArt = () => <StoryArt svg={welcomeBack} width={172} height={152} className="hx-art" />;

/** The landing's cloud machine: the isometric chassis with its modules (public/landing/cmx-machine.svg). */
export function NightMark() {
  return <img className="hx-mark" src="/landing/cmx-machine.svg" width={240} height={165} alt="" />;
}

/** The form under a sign-in headline, plain as the Mac app's (SetupWelcome.swift): no window around it, just
 *  the fields and the button at the column's width. `.wl-win` keeps the landing's field and button styles. */
export function HxWindow({ children }: { children: ReactNode }) {
  return <div className="wl-win">{children}</div>;
}

/** Adds .is-in to .hx-reveal children as they scroll into view (once each). */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll<HTMLElement>('.hx-reveal'));
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((e) => e.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);
  return ref;
}

/** The page chrome for sign-in pages and Getting ready: wordmark top left, content centered. */
export function HxPage({ children, top, steps, testid }: { children: ReactNode; top?: ReactNode; steps?: ReactNode; testid?: string }) {
  return (
    <div className="hx hx-page" data-testid={testid}>
      <div className="hx-page-bg" aria-hidden="true" />
      <header className="hx-page-top">
        <HxBrand />
        {top && <div className="hx-page-actions">{top}</div>}
      </header>
      <main className="hx-page-main" id="main">
        {children}
      </main>
      {steps && <div className="hx-page-steps">{steps}</div>}
    </div>
  );
}
